"use client";
import { AntdRegistry } from "@ant-design/nextjs-registry";
import { ConfigProvider } from "antd";
import { usePathname } from "next/navigation";
import { ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import { Provider as JotaiProvider } from "jotai";
import { appStore } from "../../Orchestrator/Recoils/store";
import { registerIntlMessages } from "../../Lib/Utils/i18n";
import { getRuntimeConfig } from "../../Lib/Config/RuntimeConfig";
import { LoaderProvider } from "../../core/contexts/LoaderContext";
import { _eventBus } from "../../core/events/EventBus";
import { mainAppStartupFlowExecute } from "../../Flows/MainAppPage/MainAppStartFlow";
import { getSession } from "../../Lib/Utils/SessionStorageUtil";
import { IRecoil } from "../../Orchestrator/Types/Index";
import { LocaleProvider, useLocale } from "../Context/LocaleContext";
import dynamic from "next/dynamic";
import CookieConsent from "../CookieConsent/CookieConsent";
import { useRoutingHook } from "../CustomHooks/routeChange";

const FeedbackOverlay = dynamic(() => import("../TesterFeedback/FeedbackOverlay"), { ssr: false });
const LiveChatWidget = dynamic(() => import("../LiveChat/LiveChatWidget"), { ssr: false });
import Footer from "../Footer/Footer";
import GetAppBanner from "../GetAppBanner/GetAppBanner";
import LegalDocModal from "../Legal/LegalDocModal";
import GlobalLoader from "../Loader/GlobalLoader";
import Section from "../Section/Section";
import { TopBar } from "../Topbar/Topbar";
import { ISupportedLocales } from "../../Orchestrator/Types/Interface/ICustomer";
import "dayjs/locale/en-gb";
import stopManager from "../../Lib/Utils/StopManager";
import { getPlaces } from "../../app/api/getPlaces";
import AntDConfigProvider from "../AntDConfigProvider/AntDConfigProvider";
import ticketOptionManager from "../../Lib/Utils/TicketOptionManager";
import { setupSearchStateAtomTabCloseListener, checkAndResetSearchStateAtomOnTabClose } from "../../Lib/Utils/SearchStateAtomResetUtil";
import { initCmsStaleRefresh } from "../../Lib/Utils/CmsStaleRefresh";
import { hasCookieConsent, getCookiePreferences, updateCookiePreferences } from "../../Lib/Utils/CookieConsentUtils";

const ClientProvider: React.FC<{
  children: React.ReactNode;
  locale: ISupportedLocales;
  messages: Record<string, string>;
}> = ({ children, messages }) => {
  const pathname = usePathname();
  const { locale } = useLocale();

  // Make the active locale's messages available to the imperative i18n path
  // (getTranslatedMessage in Actions/Services). Synchronous and idempotent, so
  // it's registered before any child renders or any flow/effect runs.
  registerIntlMessages(locale, messages);

  // The tester-feedback overlay chunk (fabric + html2canvas, ~200KB gz) must
  // only be fetched when the feature is enabled for the environment.
  const [isFeedbackEnabled, setIsFeedbackEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getRuntimeConfig()
      .then((cfg) => {
        if (!cancelled && cfg?.NEXT_PUBLIC_FEEDBACK_ENABLED === "true") {
          setIsFeedbackEnabled(true);
        }
      })
      .catch(() => {
        // Overlay is non-essential — failing silently keeps it disabled.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Hide the entire site chrome (header, footer, cookie banner) on payment
  // and legal pages. Legal pages are embedded inside the mobile app's WebView
  // so the surrounding "Get App / TopBar / nav" chrome would be redundant.
  const isLegalPage =
    pathname === "/terms-and-conditions" || pathname === "/cookie-policy";
  const shouldHideHeader =
    pathname?.includes("/checkout/payment") || isLegalPage;
  const shouldHideCookieBanner = shouldHideHeader;

  useLayoutEffect(() => {
    if (typeof window !== "undefined") {
      checkAndResetSearchStateAtomOnTabClose();
    }
  }, []);

  // Added the custom hook to listen for route changes
  useRoutingHook();
  useEffect(() => {
    const evt = _eventBus.on<IRecoil, void>({
      eventName: "GET_REC_STATE",
      handler: async ({ data }) => {
        if (data)
          _eventBus.emit({
            eventName: "GET_REC_STATE_RES",
            data: { key: data.key, value: data.value },
          });
      },
    });

    return () => {
      evt.off();
    };
  }, []);

  // The initial splash is now dismissed by LoaderProvider itself once the shell
  // mounts (see LoaderContext) — this avoids a listen-before-dispatch race where
  // a layout-effect dispatch fired before the provider's passive listener
  // attached, leaving the splash stuck.

  // Stops + ticket options still load in the background once the token is ready.
  // They populate atoms the search bar reads; they no longer gate the splash.
  useEffect(() => {
    let done = false;
    const fetchSearchMeta = () => {
      if (done) return;
      done = true;
      void stopManager.fetchStops();
      void ticketOptionManager.fetchTicketOptions();
      getPlaces().catch(() => {});
    };
    const sub = _eventBus.on({ eventName: "TOKEN_READY", handler: fetchSearchMeta });
    return () => { sub.off(); };
  }, []);

  /**
   * This userEffect manage the cache data remove operation.
   * This is because, If Guest user close the browser or close the tab
   * At that time we need to remove guest booking data.
   * This is used for Guest Checkout and Guest Retrieve booking
   */
  useEffect(() => {
    // Use `pagehide` instead of the deprecated `unload` event: `unload`
    // disqualifies the page from the back/forward cache (bf-cache) and is
    // flagged by Lighthouse's deprecation audit. `pagehide` fires in the same
    // situations (tab/browser close, navigation) but is bf-cache friendly.
    const handlePageHide = () => {
      localStorage.removeItem("retrieveBookingCacheData");
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  useEffect(() => {
    setupSearchStateAtomTabCloseListener();
  }, []);

  // Stale-while-revalidate: when a background CMS refresh finds changed
  // content, re-apply it to the widgets' atoms so the display updates in place.
  useEffect(() => initCmsStaleRefresh(), []);

  useEffect(() => {
    // Don't start flows on the password-protection page
    if (pathname === "/password") return;
    mainAppStartupFlowExecute("TopBarFlow");
    mainAppStartupFlowExecute("BannerFLow");
    mainAppStartupFlowExecute("CookiesFlow");

    // Landing content is public CMS data — start it now, in parallel, instead of
    // serializing it behind the session check. Token-gated steps (e.g. best-prices)
    // wait for the token internally via ApiRequest's token queue.
    if (pathname === "/" || pathname === "/search") {
      mainAppStartupFlowExecute("HomePageFlow");
    }
  }, []);

  useEffect(() => {
    // Returning user: re-apply their saved consent so GTM/gtag leave the
    // denied defaults set in layout.tsx. This replays BOTH categories —
    // statistic (which boots analytics via enableAnalyticsCookies → initAnalytics)
    // and marketing, which previously was never restored on load, leaving
    // ad_storage denied for users who had accepted it.
    // New users are applied from saveCookieConsent on grant.
    if (hasCookieConsent()) {
      updateCookiePreferences(getCookiePreferences());
    }
  }, []);

  useEffect(() => {
    // Don't start flows on the password-protection page
    if (pathname === "/password") return;

    const closeFlows = (...flowNames: string[]) => {
      flowNames.forEach(name => _eventBus.emit({ eventName: "FLOW_CLOSE", data: name }));
    };

    const fetchSession = async () => {
      const session = await getSession();
      //if (session?.token) {
      if (pathname === "/" || pathname === "/search") {
        closeFlows("MainBookingFlow", "CheckoutFlow", "OffersFlow", "AfterSalesBookingFlow");
        // HomePageFlow now launched in the immediate startup effect above.
      } else if (pathname === "/train-offer") {
        closeFlows("CheckoutFlow", "OffersFlow", "AfterSalesBookingFlow", "HomePageFlow");
        mainAppStartupFlowExecute("MainBookingFlow");
      } else if (pathname === "/checkout") {
        closeFlows("MainBookingFlow", "OffersFlow", "AfterSalesBookingFlow", "HomePageFlow");
        const checkoutFromWithLogin = sessionStorage.getItem(
          "checkoutFromWithLogin"
        );
        /**
         * @var checkoutFromWithLogin: string | null
         * This is because, from here checkout flow will start if pathname is /checkout
         * If checkoutFromWithLogin is "true" does it means someone else is already started checkout flow so no need to start again.
         */
        mainAppStartupFlowExecute("CheckoutFlow");
        sessionStorage.removeItem("checkoutFromWithLogin");
      } else if (pathname === "/bookings") {
        closeFlows("UpgradeBookingFlow", "AddShuttleTransferFlow", "GroupBookingFlow", "CheckoutFlow", "OffersFlow");
        mainAppStartupFlowExecute("AfterSalesBookingFlow");
      } else if (pathname.includes("/bookings/details")) {
        closeFlows("UpgradeBookingFlow", "AddShuttleTransferFlow", "CheckoutFlow", "OffersFlow");
        mainAppStartupFlowExecute("BookingDetailFlow");
      } else if (pathname === "/cancel" || pathname === "/cancel-shuttle") {
        closeFlows("CheckoutFlow", "OffersFlow");
        mainAppStartupFlowExecute("CancelBookingFlow");
      } else if (pathname === "/bookings/change-date") {
        closeFlows("BookingDetailFlow", "UpcomingPastBookingFlow", "AfterSalesBookingFlow", "CheckoutFlow", "OffersFlow");
        mainAppStartupFlowExecute("ChangeDateFlow");
      } else if (pathname === "/fmlm-offer") {
        mainAppStartupFlowExecute("FmlmViewFlow");
      } else if (pathname.includes("/service-request-claim")) {
        mainAppStartupFlowExecute("ServiceRequestClaimFlow");
      } else if (pathname.includes("/service-request-details")) {
        mainAppStartupFlowExecute("ServiceRequestClaimDetailsFlow");
      } else if (pathname === "/help") {
        mainAppStartupFlowExecute("NeedHelpFlow");
      } else if (pathname === "/networks-maps-stations") {
        mainAppStartupFlowExecute("NetworkMapStationFlow");
      } else if (pathname.startsWith("/networks-maps-stations/")) {
        mainAppStartupFlowExecute("NetworkDetailFlow");
      } else if (pathname === "/corporate-account") {
        mainAppStartupFlowExecute("RequestCorporateAccountFlow");
      } else if (pathname.startsWith("/bookings/upgrade-premium")) {
        mainAppStartupFlowExecute("UpgradeBookingFlow");
      } else if (pathname.startsWith("/bookings/add-shuttle")) {
        mainAppStartupFlowExecute("AddShuttleTransferFlow");
      } else if (pathname.startsWith("/group-booking")) {
        mainAppStartupFlowExecute("GroupBookingFlow");
      } else if (pathname.startsWith("/plan-multi-book")) {
        mainAppStartupFlowExecute("PlanMultiBookFlow");
      } else if (pathname.startsWith("/multi-booking")) {
        mainAppStartupFlowExecute("MultiBookingFlow");
      }
      // } else if (pathname === "/train-offer") {
      //   mainAppStartupFlowExecute("TrainOfferFlow");
      // } else if (pathname === "/checkout") {
      //   mainAppStartupFlowExecute("CheckoutFlow");
      // }
      // }
    };
    fetchSession();
  }, [pathname]);

  return (
    <IntlProvider locale={locale} messages={messages}>
      <JotaiProvider store={appStore}>
        <LoaderProvider>
          <GlobalLoader />
          <AntdRegistry>
            <AntDConfigProvider>
              {!shouldHideHeader && (
                <Section type="site-header">
                  <GetAppBanner />
                  <TopBar />
                </Section>
              )}
              {/* Primary landmark: gives assistive tech a single <main> region
                  and satisfies the `landmark-one-main` accessibility audit. */}
              <main id="main-content">{children}</main>
              {!shouldHideHeader && <Footer />}
              {/* No cookie banner on the bare MPGS payment page or legal pages */}
              {!shouldHideCookieBanner && <CookieConsent />}
              {/* Legal documents popup; not needed on the standalone legal
                  pages themselves (mobile WebView shows those directly). */}
              {!isLegalPage && <LegalDocModal />}
              {isFeedbackEnabled && <FeedbackOverlay />}
              {/* Live chat follows the same chrome rule as the header/footer:
                  no floating chat button over the bare MPGS payment page or the
                  legal pages rendered inside the mobile app's WebView. Always
                  mounted — the widget lives outside our React tree, so it has
                  to be told to hide rather than simply not rendered. */}
              <LiveChatWidget hidden={shouldHideHeader} />
            </AntDConfigProvider>
          </AntdRegistry>
        </LoaderProvider>
      </JotaiProvider>
    </IntlProvider>
  );
};

function LocaleProviderWrapper({
  children,
  locale,
  messages,
}: {
  children: ReactNode;
  locale: any;
  messages: Record<string, string>;
}) {
  return (
    <LocaleProvider initialLocale={locale}>
      <ClientProvider children={children} locale={locale} messages={messages} />
    </LocaleProvider>
  );
}

export default LocaleProviderWrapper;
